import { RefObject, useCallback, useEffect, useState } from "react";

import { useRouter } from "@lib/router";
import { ADDRESS_SEARCH } from "@utils/constants";

type TMapReturn = {
  map: H.Map | undefined;
  ui: H.ui.UI | undefined;
};

const useMap = (isScriptLoaded: boolean, mapNodeRef: RefObject<HTMLDivElement>): TMapReturn => {
  const [mapInstance, setMapInstance] = useState<H.Map>();
  const [mapUI, setMapUI] = useState<H.ui.UI>();

  const { defaultLocale } = useRouter();

  const initMap = useCallback(
    (mapNode: HTMLDivElement) => {
      const platform = new H.service.Platform({
        apikey: ADDRESS_SEARCH.ADDRESS_SEARCH_API_KEYS[defaultLocale!],
      });

      const maptypes = platform.createDefaultLayers();

      const centerLngLat = ADDRESS_SEARCH.COORDINATES[defaultLocale!];
      const [lat, lng] = centerLngLat.split(",");

      const map = new H.Map(mapNode, maptypes.vector.normal.map, {
        zoom: 6,
        center: { lng, lat },
        pixelRatio: window.devicePixelRatio || 1,
      });

      map.addEventListener(
        "pointermove",
        (event) => {
          const viewportNode = map.getViewPort().element as HTMLElement;
          if (event.target instanceof H.map.Marker) {
            viewportNode.style.cursor = "pointer";
          } else {
            viewportNode.style.cursor = "auto";
          }
        },
        false
      );

      // eslint-disable-next-line no-new
      new H.mapevents.Behavior(new H.mapevents.MapEvents(map));

      setMapInstance(map);

      const ui = H.ui.UI.createDefault(map, maptypes);
      ui.removeControl("mapsettings");
      ui.removeControl("zoom");
      ui.removeControl("scalebar");

      setMapUI(ui);

      map.addEventListener("tap", () => {
        ui.getBubbles().forEach((bub) => ui.removeBubble(bub));
      });

      return map;
    },
    [defaultLocale]
  );

  useEffect(() => {
    const mapNode = mapNodeRef.current!;
    const currentMap = isScriptLoaded || typeof H !== "undefined" ? initMap(mapNode) : null;

    const resizeHandle = () => currentMap?.getViewPort()?.resize();

    window.addEventListener("resize", resizeHandle);

    return () => {
      // Cleanup
      if (mapNode) {
        mapNode.innerHTML = "";
      }
      window.removeEventListener("resize", resizeHandle);
    };
  }, [initMap, isScriptLoaded, mapNodeRef]);

  return { map: mapInstance, ui: mapUI };
};
export default useMap;
